import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./FinancialChatUI.css";

const API = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

export default function FinancialChatUI({ accountId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const questionText = input;

    // Add user message to UI
    const userMessage = { role: "user", content: questionText };
    setMessages([...messages, userMessage]);
    setInput("");
    setLoading(true);

    // Add a temporary "thinking" message
    const thinkingMessageId = Date.now();
    const thinkingMessage = {
      id: thinkingMessageId,
      role: "assistant",
      content: "Processing your question...",
      isStreaming: true,
    };
    setMessages((prev) => [...prev, thinkingMessage]);

    try {
      const token = localStorage.getItem("auth_token");

      // Use fetch with SSE for streaming
      const response = await fetch(`${API}/chat/ask-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: questionText,
          account_id: accountId,
          conversation_history: conversationHistory,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalAnswer = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "status" || data.type === "tool_call") {
                // Update the thinking message with interim status
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === thinkingMessageId
                      ? { ...msg, content: data.message }
                      : msg
                  )
                );
              } else if (data.type === "answer") {
                // Store final answer
                finalAnswer = data;
              } else if (data.type === "error") {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === thinkingMessageId
                      ? {
                          ...msg,
                          content: data.message,
                          isStreaming: false,
                          isError: true,
                        }
                      : msg
                  )
                );
              }
            } catch (err) {
              console.error("Error parsing SSE data:", err);
            }
          }
        }
      }

      // Replace thinking message with final answer
      if (finalAnswer) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === thinkingMessageId
              ? {
                  ...msg,
                  content: finalAnswer.answer,
                  chart: finalAnswer.chart,
                  isStreaming: false,
                }
              : msg
          )
        );
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === thinkingMessageId
            ? {
                ...msg,
                content: `Sorry, I encountered an error: ${error.message}`,
                isStreaming: false,
                isError: true,
              }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const renderChart = (chartSpec) => {
    if (!chartSpec || !chartSpec.data || chartSpec.data.length === 0)
      return null;

    const chartData = chartSpec.labels.map((label, i) => ({
      name: label,
      value: chartSpec.data[i],
    }));

    if (chartSpec.type === "line") {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              label={{
                value: chartSpec.xAxisLabel,
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              label={{
                value: chartSpec.yAxisLabel,
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#8884d8"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartSpec.type === "bar") {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartSpec.type === "pie") {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(entry) =>
                `${entry.name}: $${entry.value.toLocaleString()}`
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="financial-chat-container">
      <div className="chat-header">
        <h2>💬 Financial Assistant</h2>
        <p className="chat-subtitle">Ask me anything about your portfolio</p>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <h3>👋 Hi! I'm your financial assistant.</h3>
            <p>Try asking me questions like:</p>
            <ul>
              <li>"What's my year-to-date return?"</li>
              <li>"Show me my weekly performance for the last 3 months"</li>
              <li>"What are my top holdings?"</li>
              <li>"How much did I invest in AAPL total?"</li>
            </ul>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`message message-${msg.role}`}>
            <div className="message-avatar">
              {msg.role === "user" ? "👤" : "🤖"}
            </div>
            <div
              className={`message-content ${
                msg.isStreaming ? "streaming" : ""
              }`}
            >
              <div className="message-text">{msg.content}</div>
              {msg.chart && (
                <div className="message-chart">
                  <h4 className="chart-title">{msg.chart.title}</h4>
                  {renderChart(msg.chart)}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="message message-assistant">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <textarea
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask about your portfolio... (Press Enter to send)"
          rows={1}
          disabled={loading}
        />
        <button
          className="chat-send-button"
          onClick={sendMessage}
          disabled={loading || !input.trim()}
        >
          {loading ? "⏳" : "📤"}
        </button>
      </div>
    </div>
  );
}
